import {
  Box,
  Container,
  Flex,
  FooterQmId,
  Grid,
  Heading,
  Link,
  Logo,
  MsButton,
  MsLink,
  Spacer,
  Stack,
  Text,
} from "@theme/components";
import { FooterDataProps } from "@theme/types";

// Adding a test comment 15/05/2024 1:36PM

export const Footer = ({ footerData }: { footerData: FooterDataProps }) => {
  const { EmailMarketingSignUpForm, currentMsMarket } = footerData;
  return (
    <Flex
      as="footer"
      sx={{
        backgroundColor: "black",
        color: "white",
        flexDirection: "column",
        width: "full",
        position: "relative",
      }}
    >
      <Box
        sx={{
          borderBottomColor: "grey500",
          borderBottomStyle: "solid",
          borderBottomWidth: [0, null, "default"],
          width: "full",
          position: "relative",
        }}
      >
        <Container>
          <Stack
            direction="horizontal"
            align="center"
            justify="space-between"
            sx={{ width: "full", py: ["s900", null, "s700"] }}
          >
            {footerData.valuePropositions.map((proposition) => {
              const { title, height, Icon } = proposition;
              return (
                <Flex
                  key={title}
                  sx={{
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: ["column", "column", "row"],
                  }}
                >
                  <Icon
                    sx={{
                      width: "auto",
                      height: height,
                      color: "white",
                      mr: [0, 0, "s300"],
                      mb: ["s200", "s200", 0],
                    }}
                  />

                  <Text
                    sx={{
                      fontSize: "fs14",
                      fontWeight: "medium",
                      textAlign: ["center", "center", "left"],
                      textTransform: "capitalize",
                      lineHeight: "lh100",
                    }}
                  >
                    {title}
                  </Text>
                </Flex>
              );
            })}
          </Stack>
        </Container>
      </Box>
      <Box
        sx={{
          width: "full",
          px: "s400",
        }}
      >
        <Container>
          <Flex
            sx={{
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                width: ["full", null, "1/2", "2/5"],
                mb: "s600",
                pt: [0, null, "s1100"],
              }}
            >
              <Box
                as="ul"
                sx={{
                  columnCount: ["auto", null, 2],
                  listStyle: "none",
                  textAlign: ["center", null, "left"],
                  borderBottomColor: "grey500",
                  borderBottomStyle: "solid",
                  borderBottomWidth: ["default", null, 0],
                  borderTopColor: "grey500",
                  borderTopStyle: "solid",
                  borderTopWidth: ["default", null, 0],
                  px: 0,
                  py: ["s800", null, 0],
                }}
              >
                {footerData.siteLinks.map((link, index) => {
                  const { title, href, to } = link;
                  return (
                    <Box
                      key={index}
                      as="li"
                      sx={{
                        lineHeight: [2, null, 1.75],
                      }}
                    >
                      {to ? (
                        <MsLink
                          to={to}
                          title={title}
                          variant="textLinkPrimaryDark"
                          sx={{
                            fontSize: ["fs14", null, "fs16"],
                            textDecoration: "none",
                          }}
                        >
                          {title}
                        </MsLink>
                      ) : (
                        <Link
                          rel={currentMsMarket !== "blog" ? "noopener" : undefined}
                          href={href}
                          title={title}
                          target={currentMsMarket !== "blog" ? "_blank" : undefined}
                          variant="textLinkPrimaryDark"
                          sx={{
                            fontSize: ["fs14", null, "fs16"],
                            textDecoration: "none",
                          }}
                        >
                          {title}
                        </Link>
                      )}
                    </Box>
                  );
                })}

                {footerData.currentMsMarket === "us" && (
                  <Box
                    as="li"
                    id="refer-button"
                    sx={{
                      lineHeight: [2, null, 1.75],
                      color: "grey300",
                      a: {
                        fontSize: ["fs14", null, "fs16"],
                        color: "grey300",
                        textDecoration: "none",
                        ":hover": { color: "goldPrimary" },
                      },
                    }}
                  />
                )}
                <Box
                  as="li"
                  sx={{
                    lineHeight: "lh400",
                  }}
                >
                  <MsButton
                    variant="textLinkLevel3Dark"
                    onClick={() => window.ketch && window.ketch("showPreferences")}
                    sx={{
                      fontSize: ["fs14", null, "fs16"],
                      textDecoration: "none",
                    }}
                  >
                    {footerData.headers.footerPrivacyPreferences}
                  </MsButton>
                </Box>
              </Box>

              <Spacer space="s700" />

              <Box sx={{ textAlign: ["center", null, "left"] }}>
                <Text
                  as="h2"
                  sx={{ color: "white", fontWeight: "medium", fontSize: ["fs20", null, "fs18"] }}
                >
                  {footerData.headers.footerAcceptedPaymentMethods}
                </Text>

                <Flex
                  as="ul"
                  sx={{
                    alignItems: "center",
                    justifyContent: ["center", null, "flex-start"],
                    listStyle: "none",
                    px: 0,
                    mt: "s200",
                  }}
                >
                  {footerData.availablePaymentMethods.map((LogoSvg, index) => {
                    return (
                      <Box as="li" key={index} sx={{ width: 50, mr: "s200" }}>
                        <LogoSvg sx={{ width: "full" }} />
                      </Box>
                    );
                  })}
                </Flex>
              </Box>

              {footerData.currentMsMarket === "ca" && (
                <Box
                  sx={{
                    color: "grey400",
                    fontSize: "fs14",
                    textAlign: ["center", null, "left"],
                    pt: "s200",
                  }}
                >
                  <Box as="p">{footerData.manscapedTitle}, Inc.</Box>
                  <Box as="p">10054 Old Grove Road, San Diego, CA 92131</Box>
                  <Box as="p">(888) 696-6205</Box>
                  <Link
                    href={`mailto: ${footerData.SUPPORT_EMAIL}`}
                    variant="textLinkLevel3"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {footerData.SUPPORT_EMAIL}
                  </Link>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: ["none", null, null, "block"],
                width: ["none", null, null, "1/5"],
              }}
            />

            <Box
              sx={{
                width: ["full", null, "1/2", "2/5"],
                textAlign: ["center", null, "right"],
                borderBottomColor: "grey500",
                borderBottomStyle: "solid",
                borderBottomWidth: ["default", null, 0],
                pt: [0, null, "s1100"],
                pb: ["s800", null, 0],
                pl: [0, null, "s400"],
              }}
            >
              {EmailMarketingSignUpForm && (
                <>
                  <Flex sx={{ justifyContent: ["center", null, "flex-end"] }}>
                    <Heading
                      sx={{
                        color: "white",
                        fontWeight: "medium",
                        fontSize: ["fs20", null, "fs18"],
                        fontFamily: "textSans",
                      }}
                    >
                      {footerData.headers.footerEmailTitle}
                    </Heading>
                  </Flex>

                  <Spacer />
                  <EmailMarketingSignUpForm
                    sx={{
                      ml: "auto",
                      mr: ["auto", null, 0],
                      width: ["full", 350],
                      span: {
                        textAlign: ["center", null, "left"],
                      },
                    }}
                  />
                </>
              )}

              <Spacer space="s500" />

              <Flex
                data-testid="social-links"
                sx={{
                  alignItems: "center",
                  justifyContent: ["center", null, "flex-end"],
                  width: ["full", 350],
                  ml: "auto",
                  mr: ["auto", null, "initial"],
                  "> a:nth-of-type(n)": {
                    px: ["s200", null, "s300"],
                    mb: [0, null, "s200"],
                  },
                  "> a:first-of-type": { pl: 0 },
                  "> a:last-of-type": { pr: 0 },
                  "> a": {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  },
                }}
              >
                {/* TODO: check if twitch icon has to have white in the logo */}
                {footerData.socialLinks.map((link) => {
                  const { title, href, Icon } = link;
                  return (
                    <Link
                      key={title}
                      rel={currentMsMarket !== "blog" ? "noopener" : undefined}
                      href={href}
                      title={title}
                      target={currentMsMarket !== "blog" ? "_blank" : undefined}
                      sx={{
                        width: "auto",
                        color: "grey400",
                        ":hover": {
                          color: "goldPrimary",
                        },
                        "> svg": {
                          height: [20, null, 24],
                        },
                      }}
                    >
                      <Icon />
                    </Link>
                  );
                })}
              </Flex>
            </Box>
          </Flex>
        </Container>
      </Box>

      <Box
        sx={{
          width: "full",
        }}
      >
        <Container>
          <Flex
            sx={{
              justifyContent: ["flex-start", null, null, "space-between"],
              flexWrap: "wrap",
              pt: ["s600", null, "s1100"],
              pb: ["s700", null, "s1100"],
            }}
          >
            <Grid
              sx={{
                gridTemplateColumns: ["auto", null, null, "1fr auto auto"],
                gridTemplateRows: ["30px auto auto auto", null, null, "auto"],
                columnGap: [0, null, null, "s700"],
                rowGap: [0, null, null, "s300"],
                alignItems: "center",
                justifyItems: ["center", null, null, "unset"],
                textAlign: ["center", null, null, "right"],
                width: "full",
              }}
            >
              <Logo
                sx={{
                  width: [115, null, null, 160],
                  height: [21, null, null, 28],
                  gridColumn: "1 / 1",
                  gridRow: ["1 / 1", null, null, "1 / span 2"],
                }}
              />
              <Flex
                as="ul"
                sx={{
                  gridColumn: ["1 / 1", null, null, "3 / 3"],
                  gridRow: ["2 / 2", null, null, "1 / 1"],
                  flexDirection: ["column", null, "row"],
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: ["center", null, null, "flex-end"],
                  listStyle: "none",
                  px: 0,
                  mx: 0,
                }}
              >
                {footerData.legalLinks.map((link, index) => {
                  const { title, to } = link;
                  if (!to) return;
                  return (
                    <Flex
                      key={index}
                      as="li"
                      sx={{
                        alignItems: "center",
                        textAlign: ["center", null, null, "left"],
                        lineHeight: "lh100",
                      }}
                    >
                      <MsLink
                        to={to}
                        title={title}
                        variant="textLinkLevel3Dark"
                        sx={
                          {
                            py: ["s200", null, null, 0],
                            fontSize: "fs12",
                            textAlign: "center",
                            textDecoration: "none",
                            whiteSpace: "nowrap",
                            color: "grey400",
                          } as const
                        }
                      >
                        {title}
                      </MsLink>
                      {index !== footerData.legalLinks.length - 1 && (
                        <Text
                          sx={{
                            display: ["none", null, "inline"],
                            lineHeight: 1,
                            color: "grey400",
                            mx: "s200",
                          }}
                        >
                          |
                        </Text>
                      )}
                    </Flex>
                  );
                })}
              </Flex>
              <Text
                sx={{
                  gridColumn: ["1 / 1", null, null, "2 / 2"],
                  gridRow: ["3 / 3", null, null, "1 / 1"],
                  color: "grey400",
                  fontSize: ["fs10", null, null, "fs12"],
                  lineHeight: "lh100",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  pt: ["s400", null, null, 0],
                }}
              >
                Copyright © {new Date().getFullYear()}, Manscaped.{" "}
                {footerData.headers.footerAllRightsReserved}
              </Text>
              <Text
                sx={{
                  gridColumn: ["1 / 1", null, null, "2 / span 2"],
                  gridRow: ["4 / 4", null, null, "2 / 2"],
                  color: "grey400",
                  fontSize: ["fs10", null, null, "fs12"],
                  lineHeight: ["lh400", null, null, "lh100"],
                  textAlign: ["center", null, null, "left"],
                  whiteSpace: ["normal", null, null, "nowrap"],
                  pt: ["s400", null, null, 0],
                }}
              >
                {footerData.headers.footerRegisteredSymbol}
              </Text>
            </Grid>
          </Flex>
          <Flex
            sx={{
              flexDirection: ["column", null, null, "row"],
              justifyContent: ["flex-start", null, null, "center"],
              alignContent: "center",
              flexWrap: "wrap",
              pb: "s200",
              px: "s800",
              maxWidth: 800,
              mx: "auto",
            }}
          >
            {footerData.countries.map((country) => {
              const { displayName, Icon, baseUrl } = country;
              return (
                <Link
                  href={`${baseUrl}/?redirect=1`}
                  key={displayName}
                  variant="box"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    px: "s300",
                    py: "s200",
                    fontSize: "fs12",
                    color: "grey400",
                    ":hover": {
                      color: "goldPrimary",
                    },
                    "> span": {
                      justifyContent: ["flex-start", null, null, "center"],
                    },
                  }}
                >
                  <Icon sx={{ mr: "s300", width: 20, height: "auto" }} />
                  {displayName}
                </Link>
              );
            })}
          </Flex>
          <FooterQmId />
        </Container>
      </Box>
    </Flex>
  );
};
